import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaReceipt } from 'react-icons/fa';
import NfceHomologacaoManager from '../components/NfceHomologacaoManager/NfceHomologacaoManager.jsx';
import KpiCard from '../components/KpiCard/KpiCard.jsx';
import DailyChart from '../components/DailyChart/DailyChart.jsx';
import nfceApi from '../services/nfceApi.js';
import { colors } from '../utils/colors.js';

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 2px solid ${colors.gray[100]};
  margin-bottom: 20px;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  background: transparent;
  color: ${(props) => (props.$active ? colors.primary[600] : colors.gray[500])};
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  font-size: 14px;
  border-bottom: 2px solid ${(props) => (props.$active ? colors.primary[600] : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;

  &:hover {
    color: ${colors.primary[600]};
  }
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0 0 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const BreakdownBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
`;

const BreakdownChip = styled.div`
  background: ${colors.white};
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 13px;
  color: ${colors.gray[700]};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const STATUS_LABELS = {
  na_fila: 'Na fila',
  processando: 'Processando',
  processado: 'Processado',
  aguardando_conferencia: 'Aguardando conferência',
  erro: 'Erro',
  uf_nao_suportada: 'UF não suportada',
};

function NfceInsights() {
  const [dias, setDias] = useState(30);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (periodo) => {
    setLoading(true);
    try {
      const data = await nfceApi.getInsights(periodo);
      setInsights(data);
    } catch (err) {
      setInsights(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(dias); }, [dias, load]);

  const porStatusMap = Object.fromEntries((insights?.por_status || []).map((i) => [i.status, i.total]));

  return (
    <div>
      <Grid>
        <KpiCard icon={FaReceipt} label="Notas capturadas" value={loading ? '...' : insights?.total_geral ?? 0} color={colors.primary[600]} bg={colors.primary[100]} />
      </Grid>

      <BreakdownBar>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <BreakdownChip key={key}>
            {label}: <strong>{porStatusMap[key] ?? 0}</strong>
          </BreakdownChip>
        ))}
      </BreakdownBar>

      <DailyChart
        title="Notas capturadas por dia"
        data={insights?.diario || []}
        dias={dias}
        onChangeDias={setDias}
        color={colors.primary[500]}
      />
    </div>
  );
}

export default function NfcePage() {
  const [activeTab, setActiveTab] = useState('homologacao');

  return (
    <div>
      <Title>NFCe</Title>
      <TabsContainer>
        <Tab type="button" $active={activeTab === 'homologacao'} onClick={() => setActiveTab('homologacao')}>
          Homologação por UF
        </Tab>
        <Tab type="button" $active={activeTab === 'insights'} onClick={() => setActiveTab('insights')}>
          Insights
        </Tab>
      </TabsContainer>

      {activeTab === 'homologacao' && <NfceHomologacaoManager />}
      {activeTab === 'insights' && <NfceInsights />}
    </div>
  );
}
